export const parseDateInput = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const parsed = new Date(Math.abs(value) < 1e12 ? value * 1000 : value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const numeric = Number(trimmed);
    if (!Number.isFinite(numeric)) return null;
    const parsed = new Date(Math.abs(numeric) < 1e12 ? numeric * 1000 : numeric);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(trimmed)) {
    const parsedUtc = new Date(`${trimmed}Z`);
    return Number.isNaN(parsedUtc.getTime()) ? null : parsedUtc;
  }

  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(trimmed)) {
    const normalized = trimmed.replace(/\s+/, 'T');
    const parsedUtc = new Date(`${normalized}Z`);
    return Number.isNaN(parsedUtc.getTime()) ? null : parsedUtc;
  }

  const normalizedOffset = trimmed.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  const parsed = new Date(normalizedOffset);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getRelativeTime = (date, { justNowLabel = 'Just now' } = {}) => {
  const target = parseDateInput(date);
  if (!target) return '';

  const diffMs = Date.now() - target.getTime();

  // Small clock skews should still read as "Just now", but avoid misleading
  // future timestamps as recent.
  if (diffMs < 0) {
    if (Math.abs(diffMs) < 5 * 60 * 1000) return justNowLabel;
    return target.toLocaleDateString();
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return justNowLabel;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  return target.toLocaleDateString();
};

export const getRelativeTimeFromHours = (hours, { justNowLabel = 'Just now' } = {}) => {
  const numericHours = Number(hours);
  if (!Number.isFinite(numericHours) || numericHours < 0) return '';

  if (numericHours === 0) {
    return 'Less than 1 hour ago';
  }

  if (numericHours < 1) {
    const minutes = Math.floor(numericHours * 60);
    if (minutes < 1) return justNowLabel;
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }

  const diffHours = Math.floor(numericHours);
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }

  const months = Math.floor(diffDays / 30);
  return `${months} month${months > 1 ? 's' : ''} ago`;
};

export const getTimeAgo = (date) => {
  const target = parseDateInput(date);
  if (!target) return '';

  const seconds = Math.floor((Date.now() - target.getTime()) / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  return `${Math.floor(hours / 24)}d ago`;
};
